"use client";

import { useAddToCart, useSelection } from "@frend-digital/centra/client";
import type { RouteGeoData } from "@frend-digital/centra/middleware";
import type { BaseSelection } from "@frend-digital/centra-types/selection";
import { events } from "@frend-digital/events";
import clsx from "clsx";
import { Suspense } from "react";
import { FavouriteIcon } from "@/components/icons/FavouriteIcon";
import Button from "@/components/ui/Button";
import DangerouslySetHtmlContent from "@/components/ui/InnerHTML";
import { useCartModal } from "@/features/cart/components/CartModal/CartModal";
import { SizeChartModal } from "@/features/product/components/SizeChartModal/SizeChartModal";
import VariantSelector from "@/features/product/components/VariantSelector/VariantSelector";
import useModal from "@/hooks/useModal";
import { useTranslations } from "@/i18n/client";
import { Link } from "@/i18n/routing";
import type { ProductDetails } from "@/lib/centra/formatters";
import { useWishlist } from "@/lib/centra/hooks";
import useSizes from "@/lib/centra/hooks/useSizes";
import { klaviyoTracker } from "@/lib/klaviyo";
import { ViewItemEvent } from "@/lib/utils/gtm";
import { useProductContext } from "../../ProductProvider";
import NotifyBackInStock, {
  BACK_IN_STOCK_MODAL_KEY,
} from "../NotifyBackInStockModal/NotifyBackInStockModal";
import NotifyComingSoon, {
  COMING_SOON_MODAL_KEY,
} from "../NotifyComingSoon/NotifyComingSoon";
import SizeSelector from "../SizeSelector/SizeSelector";
import { useProductButtonLabel } from "./getButtonLabel";
import styles from "./index.module.css";
import { SizeGuideButton } from "./SizeGuide";

export type Sizes = ReturnType<typeof useSizes>;

const ProductActions = ({
  productData,
  displayFindYourFit,
  displaySizeChart,
}: {
  productData: ProductDetails;
  displayFindYourFit: boolean;
  displaySizeChart: boolean;
  userdata: RouteGeoData | null;
}) => {
  const t = useTranslations();
  const { data } = useSelection();
  const { activeVariant, product } = useProductContext();

  const { toggleModal } = useModal("notify");
  const sizes = useSizes(activeVariant?.sizes);

  const buttonLabel = useProductButtonLabel({ sizes, product: activeVariant });
  const { has, add, remove } = useWishlist();
  const itemInWishlist = has(product?.itemId);

  const { mutate: addToCart, isPending: isLoading } = useAddToCart();
  const { openModal: openCartModal } = useCartModal();

  return (
    <section className={styles.root}>
      {activeVariant && (
        <ViewItemEvent
          item={activeVariant}
          price={activeVariant.priceAsNumber!}
        />
      )}
      <div className={styles.wrapper}>
        {!!product?.genders?.length && (
          <div className={styles.model}>
            <div className={styles.model__inner}>
              <Link
                className={clsx(
                  activeVariant?.gender === "woman" && styles.model__active,
                )}
                href={
                  activeVariant?.gender === "woman"
                    ? "#"
                    : `/product/${product?.genders[0]?.uri}`
                }
              >
                {t("product.woman")}
              </Link>
              <Link
                className={clsx(
                  activeVariant?.gender === "man" && styles.model__active,
                )}
                href={
                  activeVariant?.gender === "man"
                    ? "#"
                    : `/product/${product?.genders[0]?.uri}`
                }
              >
                {t("product.man")}
              </Link>
            </div>
          </div>
        )}
        <VariantSelector product={productData} />
        {!product?.comingSoon && <SizeSelector />}
        <Suspense>
          <NotifyBackInStock item={sizes.selectedSize} />
          <NotifyComingSoon item={sizes.selectedSize} />
        </Suspense>

        <div className={styles.disclaimers}>
          <span>{product?.modelDisclaimer}</span>
          {!!activeVariant && (
            <>
              <div className={styles.sizeGuideButtons}>
                {!!displaySizeChart && (
                  <Button
                    mode="link"
                    modal={"sizechart"}
                    className={styles.sizeChartButton}
                  >
                    <p>{t("sizechart.title")}</p>
                  </Button>
                )}
                {!!displayFindYourFit && (
                  <SizeGuideButton
                    sizes={sizes?.sizes}
                    productId={activeVariant.displayItemId!}
                    activeVariant={activeVariant}
                    addToCartId="add-to-cart"
                  />
                )}
              </div>
              <SizeChartModal sizeguide={productData?.sizeGuide} />
            </>
          )}
        </div>

        <div className={styles.addButtonsWrapper}>
          {buttonLabel.comingSoon || buttonLabel.notifyBackInStock ? (
            <Button
              modal={
                buttonLabel.comingSoon
                  ? COMING_SOON_MODAL_KEY
                  : BACK_IN_STOCK_MODAL_KEY
              }
              full
              className={styles.notifyMe}
            >
              {buttonLabel.label}
            </Button>
          ) : (
            <Button
              id="add-to-cart"
              onClick={() => {
                if (product?.comingSoon) {
                  toggleModal();
                  return;
                }
                if (!sizes.selectedSize?.stock) {
                  toggleModal();
                  return;
                }
                if (!sizes.selectedSize?.itemId) return;
                addToCart(
                  {
                    itemId: sizes.selectedSize.itemId,
                  },
                  {
                    onSuccess: () => {
                      openCartModal();

                      klaviyoTracker.addToCart(
                        data?.selection as BaseSelection,
                        product,
                      );
                    },
                  },
                );
              }}
              disabled={buttonLabel.disabled}
              isLoading={isLoading}
              full
              className={styles.addToCart}
            >
              {buttonLabel.label}
            </Button>
          )}
          <Button
            className={styles.addButton}
            onClick={e => {
              e.preventDefault();
              if (itemInWishlist) {
                return remove(product?.itemId);
              }
              add(product?.itemId);

              events.dispatch("add_to_wishlist", data, {
                dispatcher: "centra",
                item_id: product?.itemId,
              });
            }}
            aria-label="Add to wishlist"
          >
            <div
              className={clsx(
                itemInWishlist ? styles.removeFavourite : styles.addFavourite,
              )}
            >
              <FavouriteIcon />
            </div>
          </Button>
        </div>

        <div className={styles.disclaimers}>
          <div>{t("product.disclaimers.taxes")}</div>
          <div>{t("product.disclaimers.shipping")}</div>
        </div>

        {activeVariant?.shortDescription && (
          <div className={clsx(styles.lead, styles.leadMobile)}>
            <DangerouslySetHtmlContent
              html={activeVariant.shortDescription}
              type="html"
            />
          </div>
        )}
      </div>
    </section>
  );
};

const ProductActionsWrapper: typeof ProductActions = props => {
  const { activeVariant } = useProductContext();
  return <ProductActions key={activeVariant?.itemId} {...props} />;
};
export default ProductActionsWrapper;
